import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, GraduationCap } from 'lucide-react';
import type { TrainingStatus } from '@hcc/types';
import {
  progressQueryString,
  useTrainingProgress,
  type TrainingProgressFilter,
} from '../../hooks/useTrainingProgress';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { ProgressFilters } from '../../components/training/progress/ProgressFilters';
import { ProgressSummary } from '../../components/training/progress/ProgressSummary';
import { ProgressMatrix } from '../../components/training/progress/ProgressMatrix';
import { AttemptHistoryModal } from '../../components/training/progress/AttemptHistoryModal';

/**
 * HR training matrix: every person with a training assignment x every
 * module, live via the realtime invalidations. Filters live in the URL so
 * a filtered view can be shared; the CSV export carries them too.
 */
export function TrainingProgress() {
  const [params, setParams] = useSearchParams();
  const filter: TrainingProgressFilter = {
    module_id: params.get('module_id') || undefined,
    mobilisation_id: params.get('mobilisation_id') || undefined,
    status: (params.get('status') || undefined) as TrainingStatus | undefined,
  };
  const view = useTrainingProgress(filter);
  const [openCell, setOpenCell] = useState<{
    personId: string;
    taskId: string;
    moduleId: string;
  } | null>(null);

  function setFilter(next: TrainingProgressFilter) {
    const p = new URLSearchParams();
    if (next.module_id) p.set('module_id', next.module_id);
    if (next.mobilisation_id) p.set('mobilisation_id', next.mobilisation_id);
    if (next.status) p.set('status', next.status);
    setParams(p, { replace: true });
  }

  const openModule = view.data?.modules.find((m) => m.id === openCell?.moduleId);
  const moduleTitle = openModule?.title ?? 'Training module';
  const openCellData = openCell
    ? view.data?.people.find((p) => p.person.id === openCell.personId)?.cells[openCell.moduleId]
    : undefined;
  // Only external modules can be marked complete by staff (quizzes must be
  // taken); and only while the cell is not already passed.
  const canStaffComplete = !!openModule?.external && openCellData?.status !== 'passed';

  return (
    // Wider than the usual admin page: the matrix grows a column per module,
    // so give it the room a wide monitor has rather than scrolling early.
    <main className="mx-auto max-w-screen-2xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={GraduationCap}
        eyebrow="Training"
        title="Training progress"
        description="Who has completed which module, their scores and attempts. Click a cell for the attempt history."
        trailing={
          <a
            href={`/api/admin/training-progress/export.csv${progressQueryString(filter)}`}
            download
            className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </a>
        }
      />

      <ProgressFilters filter={filter} onChange={setFilter} />

      {view.isLoading ? (
        <SkeletonList rows={4} />
      ) : !view.data || view.data.people.length === 0 ? (
        <EmptyState
          icon={<GraduationCap className="h-6 w-6" />}
          title="No training assignments"
          description="Assign a training module via a role template or a person's record and progress will appear here."
        />
      ) : (
        <>
          <ProgressSummary view={view.data} />
          <ProgressMatrix
            view={view.data}
            onOpenCell={(personId, taskId, moduleId) => setOpenCell({ personId, taskId, moduleId })}
          />
        </>
      )}

      <AttemptHistoryModal
        personId={openCell?.personId ?? null}
        taskId={openCell?.taskId ?? null}
        moduleTitle={moduleTitle}
        graded={openModule?.graded !== false}
        canStaffComplete={canStaffComplete}
        status={openCellData?.status}
        onClose={() => setOpenCell(null)}
      />
    </main>
  );
}
